import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, AlertCircle, Eye, Ban, Users, Radio, RefreshCw, Database, Server, Power } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Loader } from '@/components/common/Loader';
import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Truncated } from '@/components/common/Truncated';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { liveApi, type LivePlayer, type LiveSnapshot, type LiveSource } from '@/api/endpoints';
import { LiveHubConnection } from '@/lib/liveHub';
import { WatchDialog, BanDialog } from './LiveActionDialogs';
import { LiveDrillDrawer } from './LiveDrillDrawer';

function fmtDuration(seconds: number): string {
  if (!seconds || seconds < 1) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function MarkBadge({ status }: { status?: string }) {
  const { t } = useTranslation();
  if (!status) return <Badge variant="outline" className="text-[10px]">{t('live.marks.noMark')}</Badge>;
  if (status === 'KNOWN_CHEATER') return <Badge variant="destructive" className="text-[10px]">{t('live.marks.banned')}</Badge>;
  if (status === 'INVESTIGATING') return <Badge className="text-[10px] bg-amber-500/20 text-amber-700 hover:bg-amber-500/30 border-amber-500/50">{t('live.marks.watching')}</Badge>;
  if (status === 'SUSPECT') return <Badge variant="secondary" className="text-[10px]">{t('live.marks.suspect')}</Badge>;
  if (status === 'ALLOWLIST') return <Badge className="text-[10px] bg-emerald-500/20 text-emerald-700 hover:bg-emerald-500/30 border-emerald-500/50">{t('live.marks.trusted')}</Badge>;
  if (status === 'CLEAN' || status === 'FALSE_POSITIVE') return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
  return <Badge variant="secondary" className="text-[10px]">{status}</Badge>;
}

function RiskPill({ level }: { level: number }) {
  if (!level || level <= 0) return <span className="text-xs text-muted-foreground">—</span>;
  const tone =
    level >= 8 ? 'bg-red-500/20 text-red-700 border-red-500/40' :
    level >= 5 ? 'bg-amber-500/20 text-amber-700 border-amber-500/40' :
                 'bg-blue-500/20 text-blue-700 border-blue-500/40';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border tabular-nums ${tone}`}>{level}</span>;
}

export default function LivePage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuthStore();
  const [snapshot, setSnapshot] = useState<LiveSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [connState, setConnState] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [filter, setFilter] = useState('');
  const [drillHwid, setDrillHwid] = useState<string | null>(null);
  const [watchTarget, setWatchTarget] = useState<LivePlayer | null>(null);
  const [banTarget, setBanTarget] = useState<LivePlayer | null>(null);
  // Fonte da verdade: 'auto' (server primario, fallback mongo), 'server' (forca server),
  // 'mongo' (forca mongo). Persistido em localStorage pra escolha do usuario sobreviver.
  const [source, setSource] = useState<LiveSource>(
    () => (localStorage.getItem('codeshield_live_source') as LiveSource) || 'auto'
  );
  const [serverAvailable, setServerAvailable] = useState<boolean | null>(null);
  const hubRef = useRef<LiveHubConnection | null>(null);

  // Snapshot inicial via REST (mais rapido que esperar o socket primeiro frame).
  // Re-puxa quando 'source' muda — broadcaster usa 'auto', mas a UI pode forcar outra.
  useEffect(() => {
    liveApi.snapshot(source).then(setSnapshot).catch(e => setErr(e?.message || 'Erro'));
  }, [source]);

  // Status da fonte (uma vez no mount + a cada 30s)
  useEffect(() => {
    const tick = () => liveApi.sourceStatus().then(s => setServerAvailable(s.serverEnabled)).catch(() => setServerAvailable(false));
    tick();
    const iv = setInterval(tick, 30_000);
    return () => clearInterval(iv);
  }, []);

  // SignalR push (3s) — sempre 'auto' (broadcaster do server faz isso).
  // Se o usuario seleciona 'server' ou 'mongo', usa polling pra respeitar a escolha.
  useEffect(() => {
    if (source !== 'auto') {
      // Polling para 'server'/'mongo' forcado
      const iv = setInterval(() => {
        liveApi.snapshot(source).then(setSnapshot).catch(() => { /* keep last */ });
      }, 4000);
      setConnState('disconnected');
      return () => clearInterval(iv);
    }

    const hub = new LiveHubConnection();
    hubRef.current = hub;
    hub.start((snap) => {
      setSnapshot(snap);
      setConnState('connected');
    }).catch(e => {
      setConnState('disconnected');
      console.warn('SignalR falhou, ativando polling:', e?.message);
      const iv = setInterval(() => {
        liveApi.snapshot('auto').then(setSnapshot).catch(() => { /* keep last */ });
      }, 8000);
      (hub as any)._pollingIv = iv;
    });
    return () => {
      const iv = (hub as any)._pollingIv;
      if (iv) clearInterval(iv);
      hub.stop();
    };
  }, [source]);

  function changeSource(next: LiveSource) {
    localStorage.setItem('codeshield_live_source', next);
    setSource(next);
  }

  async function handleKick(p: LivePlayer) {
    if (!p.clientId) {
      toast.error(t('live.actions.noClientId'), t('live.actions.noClientIdHint'));
      return;
    }
    if (!confirm(t('live.actions.confirmKick', { name: p.nickname || p.hwid.substring(0, 16) + '…', cid: p.clientId }))) return;
    try {
      await liveApi.kick(p.clientId, 'admin_kick_live');
      toast.success(t('live.actions.kicked'), `clientId ${p.clientId}`);
      liveApi.snapshot(source).then(setSnapshot);
    } catch (e: any) {
      toast.error(t('live.actions.kickFailed'), e?.response?.data?.reason || e?.message);
    }
  }

  const players = useMemo(() => {
    if (!snapshot?.players) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return snapshot.players;
    return snapshot.players.filter(p =>
      p.hwid.toLowerCase().includes(q) ||
      (p.nickname || '').toLowerCase().includes(q) ||
      (p.characterName || '').toLowerCase().includes(q) ||
      String(p.userId || '').includes(q) ||
      (p.ipAddress || '').includes(q)
    );
  }, [snapshot, filter]);

  async function handleWatched(hwid: string) {
    toast.success(t('live.actions.watched'), hwid.substring(0, 24) + '…');
    setWatchTarget(null);
    try { setSnapshot(await liveApi.snapshot()); } catch { /* socket pega */ }
  }
  async function handleBanned(hwid: string) {
    toast.error(t('live.actions.banned'), hwid.substring(0, 24) + '…');
    setBanTarget(null);
    try { setSnapshot(await liveApi.snapshot()); } catch { /* socket pega */ }
  }
  async function refreshNow() {
    try { setSnapshot(await liveApi.snapshot()); } catch (e: any) { setErr(e?.message); }
  }

  if (err && !snapshot) return <EmptyState title={t('common.error')} description={err} icon={AlertCircle} />;
  if (!snapshot) return <div className="flex items-center justify-center py-12"><Loader /></div>;

  const total = snapshot.players.length;
  const banned = snapshot.players.filter(p => p.mark?.status === 'KNOWN_CHEATER').length;
  const watched = snapshot.players.filter(p => p.mark?.status === 'INVESTIGATING').length;
  const suspect = snapshot.players.filter(p => p.mark?.status === 'SUSPECT' || (!p.mark && p.detectionsRecentTotal > 0)).length;

  return (
    <div>
      <PageHeader
        title={t('nav.live')}
        subtitle={
          snapshot.source === 'server'
            ? t('live.subtitleServer', { count: total, time: new Date(snapshot.generatedAtUtc).toLocaleTimeString() })
            : snapshot.source === 'server_unavailable'
              ? t('live.subtitleServerUnavailable')
              : t('live.subtitleMongo', { count: total, seconds: snapshot.windowSeconds, time: new Date(snapshot.generatedAtUtc).toLocaleTimeString() })
        }
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border bg-card p-0.5">
              <Button size="sm" variant={source === 'auto' ? 'default' : 'ghost'} className="h-7 px-2 text-xs"
                title={t('live.source.tooltipAuto')}
                onClick={() => changeSource('auto')}>
                {t('live.source.auto')}
              </Button>
              <Button size="sm" variant={source === 'server' ? 'default' : 'ghost'} className="h-7 px-2 text-xs gap-1"
                title={serverAvailable === false ? t('live.source.tooltipServerDisabled') : t('live.source.tooltipServer')}
                onClick={() => changeSource('server')}
                disabled={serverAvailable === false}>
                <Server className="h-3 w-3" /> {t('live.source.server')}
              </Button>
              <Button size="sm" variant={source === 'mongo' ? 'default' : 'ghost'} className="h-7 px-2 text-xs gap-1"
                title={t('live.source.tooltipMongo')}
                onClick={() => changeSource('mongo')}>
                <Database className="h-3 w-3" /> {t('live.source.mongo')}
              </Button>
            </div>
            <Badge variant={connState === 'connected' ? 'default' : 'secondary'} className="gap-1">
              <Radio className={`h-3 w-3 ${connState === 'connected' ? 'animate-pulse' : ''}`} />
              {connState === 'connected' ? t('live.conn.realtime') : connState === 'connecting' ? t('live.conn.connecting') : (source === 'auto' ? t('live.conn.polling') : t('live.conn.manual'))}
            </Badge>
            <Button variant="ghost" size="sm" onClick={refreshNow}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="kpi-card">
          <div className="flex items-start justify-between">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('live.kpis.online')}</div>
            <Users className="h-4 w-4 text-info" />
          </div>
          <div className="text-2xl font-bold mt-2 tabular-nums">{total}</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-start justify-between">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('live.kpis.bannedOnline')}</div>
            <Ban className="h-4 w-4 text-destructive" />
          </div>
          <div className={`text-2xl font-bold mt-2 tabular-nums ${banned ? 'text-destructive' : ''}`}>{banned}</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-start justify-between">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('live.kpis.watching')}</div>
            <Eye className="h-4 w-4 text-warning" />
          </div>
          <div className={`text-2xl font-bold mt-2 tabular-nums ${watched ? 'text-warning' : ''}`}>{watched}</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-start justify-between">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('live.kpis.withDetections')}</div>
            <Activity className="h-4 w-4 text-warning" />
          </div>
          <div className={`text-2xl font-bold mt-2 tabular-nums ${suspect ? 'text-warning' : ''}`}>{suspect}</div>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <Input
            placeholder={t('live.table.filter')}
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="mb-3"
          />
          {players.length === 0 ? (
            <EmptyState title={t('live.table.empty')} description={t('live.table.emptyHint')} icon={Users} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">{t('live.table.status')}</th>
                    <th className="px-2 py-2 text-left font-medium">{t('live.table.nickname')}</th>
                    <th className="px-2 py-2 text-left font-medium">{t('live.table.hwid')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('live.table.userId')}</th>
                    <th className="px-2 py-2 text-left font-medium">{t('live.table.ip')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('live.table.duration')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('live.table.detections')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('live.table.risk')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('live.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {players.map(p => {
                    const isBanned = p.mark?.status === 'KNOWN_CHEATER';
                    return (
                      <tr
                        key={p.hwid}
                        className={`hover:bg-muted/40 cursor-pointer ${isBanned ? 'bg-destructive/5' : ''}`}
                        onClick={() => setDrillHwid(p.hwid)}
                      >
                        <td className="px-2 py-2"><MarkBadge status={p.mark?.status} /></td>
                        <td className="px-2 py-2">
                          <div className="font-medium">{p.nickname || p.characterName || <span className="text-muted-foreground">—</span>}</div>
                          {p.characterName && p.nickname && p.characterName !== p.nickname && (
                            <div className="text-xs text-muted-foreground">{p.characterName}</div>
                          )}
                        </td>
                        <td className="px-2 py-2 font-mono text-xs"><Truncated value={p.hwid} maxChars={20} /></td>
                        <td className="px-2 py-2 text-right tabular-nums text-xs">{p.userId || <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 font-mono text-xs">{p.ipAddress || '—'}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-xs">{fmtDuration(p.sessionDurationSeconds)}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-xs">
                          {p.detectionsRecentTotal > 0 ? (
                            <span className={p.detectionsRecent.cheat > 0 ? 'text-destructive font-medium' : ''}>{p.detectionsRecentTotal}</span>
                          ) : '—'}
                        </td>
                        <td className="px-2 py-2 text-right"><RiskPill level={p.maxRiskLevel} /></td>
                        <td className="px-2 py-2 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                            <Button size="sm" variant="ghost" title={t('live.actions.drill')} onClick={() => setDrillHwid(p.hwid)}>
                              <Activity className="h-3.5 w-3.5" />
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'manager') && (
                              <Button size="sm" variant="ghost" title={t('live.actions.watch')} onClick={() => setWatchTarget(p)}>
                                <Eye className="h-3.5 w-3.5 text-amber-600" />
                              </Button>
                            )}
                            {isAdmin?.() && p.clientId && (
                              <Button size="sm" variant="ghost" title={t('live.actions.kick')} onClick={() => handleKick(p)}>
                                <Power className="h-3.5 w-3.5 text-orange-600" />
                              </Button>
                            )}
                            {isAdmin?.() && !isBanned && (
                              <Button size="sm" variant="ghost" title={t('live.actions.ban')} onClick={() => setBanTarget(p)}>
                                <Ban className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {drillHwid && <LiveDrillDrawer hwid={drillHwid} onClose={() => setDrillHwid(null)} />}
      {watchTarget && <WatchDialog player={watchTarget} onClose={() => setWatchTarget(null)} onDone={handleWatched} />}
      {banTarget && <BanDialog player={banTarget} onClose={() => setBanTarget(null)} onDone={handleBanned} />}
    </div>
  );
}
